/**
 * Caisse inactive : condition pour exécuter une commande d'assistance qui ferme, recharge
 * ou met à jour l'application. Une vente en cours ne doit jamais être coupée à distance.
 *
 * Inactive = panier vide, aucune fenêtre ouverte (paiement, ticket, clôture…), pas sur
 * l'écran monnayeur, et personne n'a touché l'écran depuis 2 minutes.
 */

import { createInteractionTracker } from '@/lib/sw-update-policy';

export const IDLE_QUIET_MS = 2 * 60 * 1000;
const CART_KEY_PREFIX = 'posvision_cart:';
const BUSY_PATH_PREFIXES = ['/monnayeur'];

export type CaisseIdleProbe = {
  now: () => number;
  lastInteractionAt: () => number;
  hasOpenDialog: () => boolean;
  hasCartItems: () => boolean;
  pathname: () => string;
};

export type IdleVerdict = { idle: true } | { idle: false; reason: string };

export function evaluateCaisseIdle(probe: CaisseIdleProbe): IdleVerdict {
  if (probe.hasCartItems()) return { idle: false, reason: 'panier en cours' };
  if (probe.hasOpenDialog()) return { idle: false, reason: 'fenêtre ouverte' };
  const path = probe.pathname();
  if (BUSY_PATH_PREFIXES.some((p) => path.startsWith(p))) {
    return { idle: false, reason: 'écran monnayeur' };
  }
  if (probe.now() - probe.lastInteractionAt() < IDLE_QUIET_MS) {
    return { idle: false, reason: 'écran utilisé récemment' };
  }
  return { idle: true };
}

/** Un panier illisible compte comme rempli : dans le doute, on ne ferme pas la caisse. */
export function hasStoredCartItems(storage: Storage): boolean {
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key?.startsWith(CART_KEY_PREFIX)) continue;
    try {
      const items: unknown = JSON.parse(storage.getItem(key) ?? '[]');
      if (!Array.isArray(items) || items.length > 0) return true;
    } catch {
      return true;
    }
  }
  return false;
}

const OPEN_DIALOG_SELECTOR =
  '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

/** Sonde réelle du navigateur ; le suivi des interactions démarre à la création. */
export function createBrowserIdleProbe(): CaisseIdleProbe {
  const tracker = createInteractionTracker();
  return {
    now: Date.now,
    lastInteractionAt: tracker.lastInteractionAt,
    hasOpenDialog: () => document.querySelector(OPEN_DIALOG_SELECTOR) !== null,
    hasCartItems: () => {
      try {
        return hasStoredCartItems(window.localStorage);
      } catch {
        return true;
      }
    },
    pathname: () => window.location.pathname,
  };
}
